import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft, UserCheck } from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { LeaveRequestStatus } from "@/modules/hr/domain/contracts";
import { LEAVE_LIST_SEARCH_DEFAULTS } from "@/modules/hr/presentation/constants";
import { useLineManagerLeaveRequestsQuery } from "../api/queries";
import { LineManagerLeaveRequestsTable } from "../ui/LineManagerLeaveRequestsTable";

type LineManagerSortBy =
  | "employeeName"
  | "leaveTypeName"
  | "startDate"
  | "createdAt";

export function LeaveLineManagerListPage() {
  const navigate = useNavigate({ from: "/app/hr/leave/line-manager" });
  const search = useSearch({ from: "/app/hr/leave/line-manager" });

  const statusUi = search?.status ?? "pending_line";
  const offset = search?.offset ?? 0;
  const limit = search?.limit ?? 20;
  const sortBy = search?.sortBy as LineManagerSortBy | undefined;
  const sortOrder = search?.sortOrder as "asc" | "desc" | undefined;

  const apiStatus: LeaveRequestStatus | undefined =
    statusUi === "all" ? undefined : (statusUi as LeaveRequestStatus);

  const listQuery = useLineManagerLeaveRequestsQuery({
    limit,
    offset,
    status: apiStatus,
    sortBy:
      sortBy &&
      ["employeeName", "leaveTypeName", "startDate", "createdAt"].includes(
        sortBy,
      )
        ? sortBy
        : undefined,
    sortOrder,
  });

  const data = listQuery.data?.data ?? [];
  const total = listQuery.data?.meta.total ?? 0;

  const persist = (patch: Partial<typeof search>) =>
    navigate({
      search: {
        ...search,
        ...patch,
      },
    });

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <UserCheck className="size-7 text-muted-foreground" aria-hidden />
              <h1 className="font-bold text-2xl tracking-tight">
                ອະນຸມັດລາ (ທີມ)
              </h1>
            </div>
            <p className="mt-1 max-w-xl text-muted-foreground text-sm">
              ຄຳຂໍລາຂອງລູກທີມ — ອະນຸມັດຂັ້ນຫົວໜ້າສາຍງານ ກ່ອນສົ່ງ HR ອະນຸມັດຂັ້ນສຸດທ້າຍ
            </p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 gap-2"
            onClick={() =>
              navigate({
                to: "/app/hr/leave/requests",
                search: LEAVE_LIST_SEARCH_DEFAULTS,
              })
            }
          >
            <ArrowLeft className="size-4" />
            ຄຳຂໍລາຂອງຂ້ອຍ
          </Button>
        </div>

        <LineManagerLeaveRequestsTable
          data={data}
          isLoading={listQuery.isLoading}
          totalCount={total}
          offset={offset}
          limit={limit}
          statusFilter={statusUi}
          onStatusFilterChange={(s) =>
            persist({
              status: s === undefined ? "all" : s,
              offset: 0,
            })
          }
          onPageChange={(o, l) => persist({ offset: o, limit: l })}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortingChange={(id, desc) =>
            persist({
              sortBy: id,
              sortOrder: desc ? "desc" : "asc",
              offset: 0,
            })
          }
        />
      </Main>
    </>
  );
}
